import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { SelectPagination } from "@foris/avocado-suite";
import cx from "classnames";
import { programQuery } from "../graphql/program.queries";
import { Program, DataQuery } from "@models/ISchema";
import { IParams } from "@models/IParams";
import { ISelectorProgram, IOption } from "./ISelectors";
import css from "./selectors.module.scss";

const ProgramSelect: React.FC<ISelectorProgram> = ({
  modality,
  campus,
  department,
  shift,
  filterBy,
  onCallback,
  className,
  shouldHaveFilters = true,
  label,
  placeholder,
}: ISelectorProgram) => {
  const client = useApolloClient();
  const { scenario }: IParams = useParams();
  const [valuePrograms, setValuePrograms] = useState(null);
  const [page, setPage] = useState(0);
  const [prevSearchTerm, setPrevSearchTerm] = useState("");
  let filterProgram = null;

  switch (filterBy) {
    case "shift":
      filterProgram = shift;
      break;
    case "modality":
      filterProgram = modality;
      break;
    case "department":
      filterProgram = department;
      break;
  }
  const [prevFilter, setPrevFilter] = useState(filterProgram);
  const [disabled, setDisabled] = useState(false);

  const getOptions = useCallback((data: any) => {
    if (data && data.data) {
      const dataQuery: DataQuery = data.data.data;
      const optPrograms = [];
      if (dataQuery.programs.items.length > 0) {
        dataQuery.programs.items.forEach((program: Program) => {
          optPrograms.push({
            label: `${program.code} - ${program.name.replace(/-/g, "")}`,
            value: program.id,
          });
        });
      }

      return {
        options: optPrograms,
        hasMore: dataQuery.programs.pageInfo.hasNextPage,
        additional: { page },
      };
    }

    return {
      options: [],
      hasMore: false,
      additional: { page },
    };
  }, []);

  const getProgramsOptions = useCallback(
    async (page?: number, searchTerm?: string) => {
      try {
        const campusObj = campus ? { campusId: { eq: campus } } : {};
        const departmentObj = department ? { departmentId: { eq: department } } : {};
        const modalityObj = modality ? { modalityId: { eq: modality } } : {};
        const shiftObj = shift ? { shiftId: { eq: shift } } : {};
        const variables = [];

        switch (filterBy) {
          case "shift":
            variables.push(campusObj, departmentObj, modalityObj, shiftObj);
            break;
          case "modality":
            variables.push(campusObj, departmentObj, modalityObj);
            break;
          case "department":
            variables.push(campusObj, departmentObj);
            break;
        }

        const fields = [...variables].filter(filter => !!Object.values(filter).length);

        const data = await client.query({
          query: programQuery,
          variables: {
            scenarioId: scenario,
            filter: {
              fields:
                fields?.length > 0
                  ? {
                      and: [...variables].filter(filter => !!Object.values(filter).length),
                    }
                  : undefined,
              pagination: {
                page,
                size: 10,
                searchTerm,
              },
            },
          },
        });

        return getOptions(data);
      } catch (error) {
        console.log(error);
      }
    },
    [client, getOptions, scenario, campus, department, modality, shift, filterBy],
  );

  const clearSelect = useCallback(() => {
    setPrevFilter(null);
    setValuePrograms("");
    setDisabled(true);
  }, []);

  const getDataByFilter = useCallback(
    async (filter?: any, page?: number, searchTerm?: string) => {
      if (shouldHaveFilters) {
        if (filter && filter !== prevFilter) {
          setPrevFilter(filter);
          const response = await getProgramsOptions(page, searchTerm);
          setDisabled(false);
          return response;
        }
        if (!filter) clearSelect();
      }

      if (!shouldHaveFilters) {
        const response = await getProgramsOptions(page, searchTerm);
        setDisabled(false);
        return response;
      }
    },
    [getProgramsOptions, prevFilter, clearSelect, shouldHaveFilters],
  );

  const loadOptions = async (newSearchTerm: string) => {
    const newSearchPage = !newSearchTerm || prevSearchTerm === newSearchTerm ? page + 1 : 1;

    setPrevSearchTerm(newSearchTerm);
    setPage(newSearchPage);

    let response = null;

    switch (filterBy) {
      case "shift":
        response = await getDataByFilter(shift, newSearchPage, newSearchTerm);
        break;
      case "modality":
        response = await getDataByFilter(modality, newSearchPage, newSearchTerm);
        break;
      case "department":
        response = await getDataByFilter(department, newSearchPage, newSearchTerm);
        break;
    }

    return {
      options: response?.options || [],
      hasMore: response?.hasMore || false,
      additional: { page: newSearchPage },
    };
  };

  useEffect(() => {
    if (!["shift", "modality", "department"].includes(filterBy)) {
      clearSelect();
    }
  }, [modality, campus, department, shift, filterBy, clearSelect, getDataByFilter]);

  return (
    <div className={cx(css.selectors, className)}>
      <SelectPagination
        key={`${campus}-${department}-${modality}-${shift}`}
        loadOptions={loadOptions}
        label={label}
        value={valuePrograms}
        placeholder={placeholder}
        isClearable
        disabled={disabled}
        debounceTimeout={500}
        onChange={(value: IOption) => {
          if (value) {
            setValuePrograms(value);
            onCallback(value);
          } else {
            setValuePrograms("");
            onCallback(value);
          }
        }}
      />
    </div>
  );
};

export default ProgramSelect;
